/**
 * userStore — état global de l'utilisateur authentifié et de ses préférences.
 *
 * Persistance localStorage via zustand/persist (clé "unique-user").
 * Le token JWT est stocké ici et injecté dans tous les appels API.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Plan, UserPreferences, UserProfile } from './types';
import { AuthService, type AuthTokenResponse } from '@services/AuthService';

interface UserState {
  profile: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;

  /** Connexion réelle via le backend FastAPI. */
  login: (email: string, password: string) => Promise<void>;
  /** Inscription via le backend FastAPI. */
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  /** Met à jour le profil après un changement de plan. */
  refreshPlan: () => Promise<void>;
  setProfile: (profile: UserProfile, token?: string) => void;
  updatePreferences: (patch: Partial<UserPreferences>) => void;
  logout: () => void;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  defaultFocusMinutes: 25,
  dayStart: '08:00',
  locale: 'fr-BF',
  notificationsEnabled: true,
};

function tokenResponseToProfile(data: AuthTokenResponse['user']): UserProfile {
  return {
    id:           data.id,
    email:        data.email,
    firstName:    data.first_name,
    lastName:     data.last_name,
    role:         'manager',
    plan:         (data.plan ?? 'free') as Plan,
    preferences:  DEFAULT_PREFERENCES,
  };
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      profile:         null,
      token:           null,
      isAuthenticated: false,

      login: async (email, password) => {
        const res = await AuthService.login(email, password);
        const profile = tokenResponseToProfile(res.user);
        set({ profile, token: res.access_token, isAuthenticated: true });
      },

      register: async (email, password, firstName, lastName) => {
        const res = await AuthService.register(email, password, firstName, lastName);
        const profile = tokenResponseToProfile(res.user);
        set({ profile, token: res.access_token, isAuthenticated: true });
      },

      refreshPlan: async () => {
        const { token } = get();
        if (!token) return;
        try {
          const user = await AuthService.me(token);
          set((state) => ({
            profile: state.profile
              ? { ...state.profile, plan: (user.plan ?? 'free') as Plan }
              : null,
          }));
        } catch {
          /* silencieux — le plan reste inchangé */
        }
      },

      setProfile: (profile, token) =>
        set({
          profile: {
            ...profile,
            plan: profile.plan ?? 'free',
            preferences: { ...DEFAULT_PREFERENCES, ...profile.preferences },
          },
          isAuthenticated: true,
          ...(token !== undefined ? { token } : {}),
        }),

      updatePreferences: (patch) =>
        set((state) => {
          if (!state.profile) return state;
          return {
            profile: {
              ...state.profile,
              preferences: { ...state.profile.preferences, ...patch },
            },
          };
        }),

      logout: () => set({ profile: null, token: null, isAuthenticated: false }),
    }),
    {
      name: 'unique-user',
      storage: createJSONStorage(() => localStorage),
      version: 2,
    },
  ),
);

/** Retourne le token JWT courant (pour les appels API). */
export function getToken(): string | null {
  return useUserStore.getState().token;
}

export function createDemoProfile(email: string): UserProfile {
  const [local] = email.split('@');
  const parts = (local ?? 'utilisateur').split('.');
  const firstName = capitalize(parts[0] ?? 'Utilisateur');
  const lastName = capitalize(parts[1] ?? '');
  return {
    id:           'demo-user',
    email,
    firstName,
    lastName,
    role:         'manager',
    plan:         'free',
    organization: 'UNIQUE Demo',
    preferences:  DEFAULT_PREFERENCES,
  };
}

function capitalize(value: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}
